<template>
  <div v-if="amIVisible || viewable" class="asc-file-upload-file--container">
    <div :class="[showFileVisibilityToggle ? 'visibilityToggleShown' : '']">
      <div class="asc-file-upload-file--info">
        <asc-accent-button
          v-if="showFileVisibilityToggle"
          :icon-only="true"
          :disabled="disabled"
          button-text="Show/hide this file"
          :on-click="disabled ? null : handleToggleFileVisibility"
          :icon="amIVisible ? 'mdi-eye' : 'mdi-eye-off'"
          :custom-class="amIVisible ? 'visibleFile' : ''"
        ></asc-accent-button>
        <div :class="['text-name', isRemoving ? 'removing' : null]">
          <v-clamp autoresize :max-lines="1" class="clamp">
            {{ file.displayName }}
            <template v-if="isRemoving" #after>
              will be deleted in…{{
                removeFileCountDown >= 0 ? removeFileCountDown : 0
              }}
              secs
            </template>
          </v-clamp>
        </div>
        <div v-if="!isRemoving" class="text-size">
          <asc-label
            size="md"
            :value="getFileSize"
            :color="getFileSizeColor"
          ></asc-label>
        </div>
        <asc-accent-button
          v-if="isRemoving"
          button-text="Undo"
          icon-position="left"
          icon="mdi-replay"
          :on-click="handleRemoveFileUndo"
          custom-class="undo"
        ></asc-accent-button>
        <div v-if="!isRemoving" class="icon">
          <v-menu
            bottom
            offset-y
            left
            :disabled="isFileLoading || disabled"
            close-on-click
            content-class="asc-file-upload-file--menu"
            :nudge-left="5"
          >
            <template #activator="{ on }">
              <v-icon
                size="16"
                :color="getFileNameColor"
                :ripple="false"
                v-on="on"
              >
                mdi-dots-vertical
              </v-icon>
            </template>

            <v-list dense class="pa-0">
              <v-list-item
                v-if="allowFileRemove"
                @click="handleRemoveFileTimer"
              >
                <v-list-item-title>Remove Attachment</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="!isFileLoading && allowFileDownload"
                @click="handleDownloadFile"
              >
                <v-list-item-title>Download</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div
        v-if="
          canEditAttachmentCaptions ||
          (previewCaption !== '' && previewCaption !== null)
        "
        class="attachment-caption"
      >
        <asc-caption
          v-if="canEditAttachmentCaptions"
          class="attachment-caption-input"
          :caption="previewCaption"
          :disabled="disabled"
          :max-length="500"
          @blur="updatePreviewCaption"
        ></asc-caption>
        <p v-if="!canEditAttachmentCaptions" class="disabled">
          {{ previewCaption }}
        </p>
      </div>
    </div>

    <template v-if="isFileLoading">
      <v-progress-linear
        indeterminate
        :color="getAccentColor"
        bottom
        :height="4"
      ></v-progress-linear>
    </template>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscLabel from '@/components/atoms/AscLabel'
import AscCaption from '@/components/molecules/AscCaption'
import variables from '@/styles/_varsJs.scss'
import { bytesToSize } from '@/utilities/file'
import VClamp from 'vue-clamp'

export default {
  name: 'AscFileUploadFile',

  components: {
    VClamp,
    AscLabel,
    AscAccentButton,
    AscCaption,
  },

  props: {
    file: {
      type: Object,
      default: () => {},
    },

    showFileVisibilityToggle: {
      type: Boolean,
      default: true,
    },
    allowFileRemove: {
      type: Boolean,
      default: true,
    },
    onRemoveFile: {
      type: Function,
      default: () => {},
    },
    showFileRemoveNotification: {
      type: Boolean,
      required: true,
    },
    removeFileNotificationCountDown: {
      type: Number,
      required: true,
    },
    allowFileDownload: {
      type: Boolean,
      default: true,
    },
    onDownloadFile: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    viewable: {
      type: Boolean,
      default: false,
    },
    canEditAttachmentCaptions: {
      type: Boolean,
      default: false,
    },
    handleUpdateAttachmentCaption: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      amIVisible: this.file.isExternallyVisible,
      previewCaption: this.getCaptionValue(),
      isRemoving: false,
      removeFileInterval: null,
      removeFileCountDown: this.removeFileNotificationCountDown,
    }
  },

  computed: {
    isFileLoading() {
      return this.file.loading
    },
    getFileSize() {
      return bytesToSize(this.file.fileSizeInBytes)
    },
    getAccentColor() {
      return variables.accent500
    },
    getFileNameColor() {
      return this.disabled ? variables.gray600 : variables.gray1000
    },
    getFileSizeColor() {
      return variables.gray600
    },
  },
  watch: {
    file(newValue) {
      this.previewCaption = this.getCaptionValue(newValue)
    },
    removeFileCountDown(value) {
      if (value === -1) this.handleRemoveFile()
    },
  },

  mounted() {
    this.previewCaption = this.getCaptionValue()
  },

  async beforeDestroy() {
    this.clearRemoveFileTimer()
  },

  methods: {
    handleRemoveFileUndo() {
      this.isRemoving = false
      this.clearRemoveFileTimer()
      this.removeFileInterval = null
      this.removeFileCountDown = this.removeFileNotificationCountDown
    },
    handleRemoveFile() {
      if (this.onRemoveFile) {
        this.onRemoveFile(this.file)
      }
    },
    handleRemoveFileTimer() {
      if (this.showFileRemoveNotification) {
        this.isRemoving = true
        this.removeFileInterval = setInterval(
          () => this.removeFileCountDown--,
          1000,
        )
      } else {
        this.handleRemoveFile()
      }
    },
    clearRemoveFileTimer() {
      if (this.removeFileInterval) clearInterval(this.removeFileInterval)
    },

    handleDownloadFile() {
      if (this.onDownloadFile) {
        this.onDownloadFile(this.file)
      }
    },
    handleToggleFileVisibility() {
      this.amIVisible = !this.amIVisible
      this.$emit('toggleFileVisibility', {
        ...this.file,
        isExternallyVisible: this.amIVisible,
      })
    },
    updatePreviewCaption(value) {
      this.previewCaption = value || null
      this.handleUpdateAttachmentCaption({
        ...this.file,
        caption: value,
      })
    },
    getCaptionValue(file = this.file) {
      return Object.prototype.hasOwnProperty.call(file, 'caption')
        ? file.caption
        : null
    },
  },
}
</script>

<style lang="scss">
.asc-file-upload-file--container {
  border: 1px solid $gray-300;
  margin-bottom: 8px;
  height: auto;

  .v-btn__content .v-icon {
    color: $gray-600;
  }

  & .attachment-caption-input {
    padding: 12px 12px 15px 12px;
  }

  & .attachment-caption {
    font-size: 14px;
    color: $gray-700;
    padding: 5px 12px 5px 0;

    .disabled {
      padding-left: 8px;
    }

    .attachment-caption-input {
      padding: 5px 12px 5px 8px;
    }
  }

  .asc-file-upload-file--info {
    padding: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 32px;

    .visibleFile .v-btn__content .v-icon {
      color: $accent-600;
    }

    &.visibilityToggleShown {
      align-items: center;
      padding: 0 4px;
    }

    .text-name {
      flex: 1;
      margin: 0 0 0 4px;
      color: $primary-black;
      font-size: $font-size-md;
      letter-spacing: 0;
      line-height: 16px;

      &.removing {
        color: $gray-700;
        font-size: $font-size-sm;
      }

      .clamp {
        overflow-wrap: break-word;
        word-wrap: break-word;
        -ms-word-break: break-all;
        word-break: break-all;
        word-break: break-word;
        -ms-hyphens: auto;
        -moz-hyphens: auto;
        -webkit-hyphens: auto;
        hyphens: auto;
      }
    }

    .asc-accent-button-container {
      margin-top: 0;

      .undo {
        padding: 0 8px !important;

        .v-btn__content {
          font-weight: normal;
          color: $accent-700;
          font-size: 12px;
          text-transform: capitalize;
          align-items: normal;

          .v-icon {
            margin-right: 4px;
            color: inherit;
            font-size: 14px;
            width: 12px;
            height: 14px;
          }
        }
      }
    }

    .text-size {
      width: 50px;
      text-align: right;
      letter-spacing: 0;
      line-height: 18px;
      font-weight: normal;

      span {
        white-space: nowrap;
      }
    }

    .icon {
      max-width: 20px;
    }

    .v-progress-linear {
      margin: 0;
      height: 4px;
    }

    .asc-accent-button-container--primary .asc-accent-button--accent.v-btn {
      background-color: initial !important;
    }

    .asc-file-upload-file--menu {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.17) !important;

      .v-list {
        padding: 0;

        .v-list-item {
          height: 34px;
          padding: 7px 10px 9px 10px;

          &:hover {
            color: inherit;
            background-color: $gray-100 !important;
            text-decoration: none;
          }

          .v-list-item__title {
            font-size: 14px;
            letter-spacing: 0;
            line-height: 18px;
          }
        }
      }
    }
  }
}
</style>
